import { useCallback, useEffect, useState } from 'react';
import { fetchDashboardData } from './api';
import type { CardDashboardResponse } from './types';

/**
 * Loads the dashboard payload on entry and keeps it fresh with a 30 s silent
 * background refresh (PRD §2 / §5 / O-8).
 *
 * Error model (PRD §9): the error phase is entered when the **initial** load
 * fails with any non-2xx, OR returns a 200 with a truthy top-level `error`
 * (partial-enrichment failure). A **background** refresh that fails (silent)
 * never wipes the last good data — it just tries again next interval; and a
 * silent refresh that succeeds recovers a stuck error state. Polling pauses
 * while the embed is hidden and resumes with an immediate fetch.
 */
export type DashboardPhase = 'loading' | 'ready' | 'error';

const REFRESH_MS = 30_000;

export const useDashboardData = (
  language: string,
): { phase: DashboardPhase; data: CardDashboardResponse | null; reload: () => void } => {
  const [phase, setPhase] = useState<DashboardPhase>('loading');
  const [data, setData] = useState<CardDashboardResponse | null>(null);

  const load = useCallback(
    async (silent = false) => {
      if (!silent) setPhase('loading');
      try {
        const res = await fetchDashboardData({ language });
        if (res.error) {
          if (silent) return; // partial failure on a background poll → keep last good
          setData(res);
          setPhase('error');
          return;
        }
        setData(res);
        setPhase('ready');
      } catch {
        if (silent) return; // transient background failure → keep last good
        setData(null);
        setPhase('error');
      }
    },
    [language],
  );

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | undefined;
    const stop = () => {
      if (timer) clearInterval(timer);
      timer = undefined;
    };
    const start = () => {
      stop();
      timer = setInterval(() => void load(true), REFRESH_MS);
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        void load(true);
        start();
      }
    };
    start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [load]);

  return { phase, data, reload: () => void load(false) };
};
